import React, { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/ZeroTrustSecurity")({
  component: ZeroTrustSecurity,
});

type Tab = "IDENTITY_AUTH" | "NETWORK_TOPOLOGY" | "AUDIT_LOGS";

function ZeroTrustSecurity() {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<Tab>("IDENTITY_AUTH");
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [accessLevel, setAccessLevel] = useState("RESTRICTED");
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [logs, setLogs] = useState<string[]>([
    "[VPC] Inbound Traffic: Port 443 - ALLOWED",
    "[AzureAD] SSO Session: active (user_admin_01)",
    "[SHIELD] Security Group: Drop all 0.0.0.0/0 on Port 22"
  ]);

  const simulateAccess = (success: boolean) => {
    setIsAuthorizing(true);
    setLogs(prev => ["[GATEWAY] Analyzing Request Headers...", ...prev]);

    setTimeout(() => {
      setIsAuthorizing(false);
      if (success && mfaEnabled) {
        setAccessLevel("GRANTED");
        setLogs(prev => ["[SUCCESS] Azure AD MFA Verified. Opening VPC Tunnel.", ...prev].slice(0, 8));
      } else {
        setAccessLevel("DENIED");
        setLogs(prev => ["[BLOCK] 401 Unauthorized: MFA Challenge Failed.", ...prev].slice(0, 8));
      }
    }, 1500);
  };

  return (
    <div style={containerStyle}>
      <nav style={navStyle}>
        <div style={navBrand} onClick={() => navigate({ to: "/Portfolio" })} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={avatarStyle}>JF</div>
          <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>CASE_STUDY_04</span>
        </div>
        <div style={navLinks}>
          <button onClick={() => navigate({ to: "/Portfolio" })} style={backButtonStyle}>
            ← BACK_TO_PORTFOLIO
          </button>
        </div>
      </nav>

      <main style={mainContent}>
        <header style={headerSection}>
          <div style={labelStyle}>CYBERSECURITY_ENGINEERING</div>
          <h1 style={titleStyle}>Zero Trust Auth & Network Hardening</h1>
          <p style={subtitleStyle}>Centralizing identity management and securing VPC infrastructure for enterprise-grade protection.</p>
          <div style={tagRow}>
            {["AZURE_AD", "VPC", "SSO", "SECURITY_GROUPS", "DNS_SEC", "MFA"].map((t) => (
              <span key={t} style={tagStyle}>{t}</span>
            ))}
          </div>
        </header>

        <section style={sectionBox}>
          <span style={sectionLabel}>01. SECURITY_ARCHITECTURE</span>
          <div style={gridTwo}>
            <div>
              <h3 style={smallHeading}>THE VULNERABILITY</h3>
              <p style={paragraph}>
                Internal services were previously accessible via basic credentials and resided on a flat network. This posed a high risk of lateral movement in the event of a breach. Domain Name integrity was also unverified, leaving the system open to spoofing.
              </p>
            </div>
            <div>
              <h3 style={smallHeading}>THE DEFENSE</h3>
              <p style={paragraph}>
                I implemented a **Zero Trust Gateway** using **Azure AD** for centralized identity. By enforcing MFA and partitioning the infrastructure into isolated **VPCs** with strict **Security Groups**, we ensured that every request is verified, regardless of its origin.
              </p>
            </div>
          </div>
        </section>

        <section style={sectionBox}>
          <span style={sectionLabel}>02. SECURITY_SIMULATION_GATEWAY</span>
          <div style={webWindow}>
            <div style={webTabRow}>
              {(['IDENTITY_AUTH', 'NETWORK_TOPOLOGY', 'AUDIT_LOGS'] as Tab[]).map(t => (
                <span key={t} onClick={() => setActiveTab(t)}
                  style={{ ...webTab, color: activeTab === t ? '#3b82f6' : '#555', borderBottom: activeTab === t ? '2px solid #3b82f6' : 'none' }}>
                  {t}
                </span>
              ))}
            </div>

            <div style={apiGridContainer}>
              <div style={{ minHeight: '320px' }}>
                {activeTab === 'IDENTITY_AUTH' && (
                  <div>
                    <div style={metricGrid}>
                      <div style={metricCard}>
                        <div style={sideLabel}>MFA_ENFORCEMENT</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" checked={mfaEnabled} onChange={() => setMfaEnabled(!mfaEnabled)} />
                          <span style={{ fontSize: '12px', color: mfaEnabled ? '#10b981' : '#f59e0b' }}>
                            {mfaEnabled ? 'ACTIVE' : 'DISABLED'}
                          </span>
                        </div>
                      </div>
                      <div style={metricCard}>
                        <div style={sideLabel}>ACCESS_STATUS</div>
                        <h2 style={{ ...metricValue, color: accessLevel === 'GRANTED' ? '#10b981' : accessLevel === 'DENIED' ? '#ef4444' : '#fff' }}>
                          {accessLevel}
                        </h2>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button style={{ ...webButton, flex: 1 }} onClick={() => simulateAccess(true)} disabled={isAuthorizing}>
                        SIMULATE_AUTHORIZED_USER
                      </button>
                      <button style={{ ...webButton, flex: 1, backgroundColor: '#333' }} onClick={() => simulateAccess(false)} disabled={isAuthorizing}>
                        SIMULATE_UNAUTHORIZED
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'NETWORK_TOPOLOGY' && (
                  <div style={{ padding: '10px' }}>
                    <h4 style={smallHeading}>VPC_SEGMENTATION</h4>
                    <div style={networkVisual}>
                      <div style={nodeBox}>PUBLIC_SUBNET (Load Balancer)</div>
                      <div style={arrowLine}>↓ Security Group: Port 443 Only</div>
                      <div style={{ ...nodeBox, borderColor: '#3b82f6' }}>PRIVATE_SUBNET (App Layer)</div>
                      <div style={arrowLine}>↓ Restricted Access</div>
                      <div style={{ ...nodeBox, borderColor: '#ef4444' }}>ISOLATED_DB_SUBNET</div>
                    </div>
                  </div>
                )}

                {activeTab === 'AUDIT_LOGS' && (
                  <div style={webLogTerminal}>
                    {logs.map((log, i) => (
                      <div key={i} style={{ 
                        color: log.includes('SUCCESS') || log.includes('ALLOW') ? '#10b981' : log.includes('BLOCK') ? '#ef4444' : '#888', 
                        marginBottom: '4px', fontSize: '11px'
                      }}>
                        {log}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={infoSidebar}>
                <div style={sideLabel}>THREAT_MITIGATION</div>
                <div style={healthBar}><div style={{ ...healthFill, width: '98%', backgroundColor: '#10b981' }}></div></div>
                <div style={{ fontSize: '10px', color: '#555', marginTop: '5px' }}>ZERO_LATERAL_RISK</div>
                
                <div style={{ marginTop: '30px' }}>
                  <div style={sideLabel}>IDENTITY_PROVIDER</div>
                  <div style={{ color: '#fff', fontSize: '11px' }}>Azure AD (Entra ID)</div>
                  <div style={{ color: '#fff', fontSize: '11px', marginTop: '10px' }}>PROTOCOL: OpenID Connect</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        

        <section style={sectionBox}>
          <span style={sectionLabel}>03. HARDENING_IMPACT</span>
          <div style={gridThree}>
            <div style={cardStyle}>
              <div style={cardNum}>01</div>
              <h4 style={cardTitle}>CENTRALIZED_IDENTITY</h4>
              <p style={cardText}>Eliminated local database password storage by offloading authentication to Azure AD, ensuring MFA is required for all internal admin panels.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>02</div>
              <h4 style={cardTitle}>NETWORK_ISOLATION</h4>
              <p style={cardText}>Established multi-tier VPCs where database instances are physically unreachable from the public internet, accessible only via proxied app-tier traffic.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>03</div>
              <h4 style={cardTitle}>DNS_INTEGRITY</h4>
              <p style={cardText}>Hardened internal DNS resolution and implemented Security Groups that follow the "Least Privilege" principle, dropping 100% of unauthorized ingress traffic.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* STYLES */
const containerStyle: React.CSSProperties = { backgroundColor: "#0a0a0a", color: "#e0e0e0", minHeight: "100vh", padding: "120px 24px 60px 24px", fontFamily: "monospace" };
const navStyle: React.CSSProperties = { position: "fixed", top: 0, left: 0, right: 0, height: "70px", backgroundColor: "rgba(10, 10, 10, 0.8)", backdropFilter: "blur(12px)", borderBottom: "1px solid #222", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 40px", zIndex: 1000 };
const navBrand: React.CSSProperties = { display: "flex", alignItems: "center", gap: "12px" };
const avatarStyle: React.CSSProperties = { width: "32px", height: "32px", backgroundColor: "#1a1a1a", border: "1px solid #333", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "12px", fontWeight: "bold", borderRadius: "8px" };
const navLinks: React.CSSProperties = { display: "flex", gap: "30px" };
const backButtonStyle: React.CSSProperties = { backgroundColor: "transparent", border: "1px solid #333", color: "#666", padding: "8px 16px", fontSize: "11px", fontWeight: "bold", cursor: "pointer" };
const mainContent: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto" };
const headerSection: React.CSSProperties = { marginBottom: "60px", borderBottom: "1px solid #1a1a1a", paddingBottom: "40px" };
const titleStyle: React.CSSProperties = { fontSize: "32px", color: "#fff", margin: "10px 0", fontWeight: "bold" };
const subtitleStyle: React.CSSProperties = { fontSize: "16px", color: "#3b82f6", marginBottom: "25px", maxWidth: "600px" };
const labelStyle: React.CSSProperties = { fontSize: "10px", color: "#666", marginBottom: "12px", letterSpacing: "1px" };
const tagRow: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const tagStyle: React.CSSProperties = { fontSize: "10px", backgroundColor: "#1a1a1a", border: "1px solid #333", padding: "4px 12px", color: "#aaa", borderRadius: "6px" };
const sectionBox: React.CSSProperties = { marginBottom: "100px" };
const sectionLabel: React.CSSProperties = { fontSize: "10px", color: "#444", letterSpacing: "2px", display: "block", marginBottom: "30px" };
const gridTwo: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" };
const smallHeading: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const paragraph: React.CSSProperties = { fontSize: "14px", color: "#888", lineHeight: "1.6", margin: 0 };

const webWindow: React.CSSProperties = { backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '30px' };
const webTabRow: React.CSSProperties = { display: 'flex', gap: '20px', borderBottom: '1px solid #222', marginBottom: '30px' };
const webTab: React.CSSProperties = { fontSize: '10px', cursor: 'pointer', paddingBottom: '12px', fontWeight: 'bold', letterSpacing: '1px' };
const apiGridContainer: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 300px', gap: '30px' };
const metricGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' };
const metricCard: React.CSSProperties = { backgroundColor: '#0a0a0a', border: '1px solid #222', padding: '20px', borderRadius: '12px' };
const metricValue: React.CSSProperties = { fontSize: "28px", color: "#fff", margin: 0 };
const webLogTerminal: React.CSSProperties = { backgroundColor: '#050505', padding: '15px', borderRadius: '8px', fontSize: '11px', height: '160px', overflowY: 'auto' };
const webButton: React.CSSProperties = { backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '14px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' };

const infoSidebar: React.CSSProperties = { borderLeft: '1px solid #222', paddingLeft: '30px' };
const sideLabel: React.CSSProperties = { fontSize: '9px', color: '#444', marginBottom: '10px' };
const healthBar: React.CSSProperties = { width: '100%', height: '4px', backgroundColor: '#222', borderRadius: '2px' };
const healthFill: React.CSSProperties = { height: '100%', backgroundColor: '#10b981', borderRadius: '2px' };

const networkVisual: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' };
const nodeBox: React.CSSProperties = { border: '1px solid #333', padding: '10px 20px', borderRadius: '8px', fontSize: '11px', width: '100%', textAlign: 'center', color: '#fff' };
const arrowLine: React.CSSProperties = { fontSize: '12px', color: '#444' };

const gridThree: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" };
const cardStyle: React.CSSProperties = { backgroundColor: "#111", padding: "30px", borderRadius: "16px", border: "1px solid #222", position: "relative" };
const cardNum: React.CSSProperties = { fontSize: "40px", color: "#1a1a1a", fontWeight: "bold", position: "absolute", top: "10px", right: "20px" };
const cardTitle: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const cardText: React.CSSProperties = { fontSize: "13px", color: "#888", lineHeight: "1.6" };

export default ZeroTrustSecurity;